import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	MODAL,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	WHAT_ARE_COMPASS_COMPONENTS_ARTICLE_ID,
	WHAT_ARE_COMPASS_COMPONENTS_SUPPORT_URL,
	WHAT_ARE_JIRA_COMPONENTS_ARTICLE_ID,
	WHAT_ARE_JIRA_COMPONENTS_SUPPORT_URL,
} from '../../constants.tsx';
import { useCompassComponentsFeedbackCollectorActions } from './feedback-collector.tsx';
import messages from './messages.tsx';

type Props = {
	isAdminContext: boolean;
	loading: boolean;
	globalComponentsEnabled: boolean;
	setGlobalComponentsProperty: (newValue: boolean) => void;
};

export function PopupContent(props: Props) {
	const { isAdminContext, loading, globalComponentsEnabled, setGlobalComponentsProperty } = props;
	const { openGlobalComponentsFeedbackCollector } = useCompassComponentsFeedbackCollectorActions();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const adminAwareToggle = useMemo(() => {
		const toggle = (
			<Toggle
				testId="project-directories.components.global-components-toggle-button.popup-content.toggle"
				isDisabled={
					// Only admins can turn on Compass components.
					!isAdminContext || loading
				}
				isChecked={globalComponentsEnabled}
				onChange={(_, analyticEvent) => {
					fireUIAnalytics(analyticEvent, 'globalComponentsProperty', {
						value: !globalComponentsEnabled,
					});
					setGlobalComponentsProperty(!globalComponentsEnabled);

					// Request feedback when global components are disabled (i.e. was enabled, becoming disabled).
					if (globalComponentsEnabled) {
						openGlobalComponentsFeedbackCollector({
							cancelButtonLabel: formatMessage(messages.skipButtonLabel),
						});
					}
				}}
			/>
		);

		if (isAdminContext) {
			return toggle;
		}

		return (
			<Tooltip
				testId="project-directories.components.global-components-toggle-button.popup-content.tooltip"
				content={formatMessage(messages.askAdmin)}
			>
				{toggle}
			</Tooltip>
		);
	}, [
		formatMessage,
		loading,
		isAdminContext,
		globalComponentsEnabled,
		setGlobalComponentsProperty,
		openGlobalComponentsFeedbackCollector,
	]);

	const actionHint = useMemo(() => {
		if (globalComponentsEnabled) {
			if (isAdminContext) {
				return messages.jiraAdminActionHint;
			}
			return messages.jiraActionHint;
		}

		if (isAdminContext) {
			return messages.compassAdminActionHint;
		}
		return messages.compassActionHint;
	}, [globalComponentsEnabled, isAdminContext]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onClickLearnMore = (_: any, analyticEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(
			analyticEvent,
			globalComponentsEnabled ? 'learnMoreAboutJiraLink' : 'learnMoreAboutCompassLink',
		);
	};

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="globalComponentsTogglePopup">
			<FireScreenAnalytics />
			<MenuGroup>
				<Section>
					<Container>
						<Header>
							<Heading size="small">
								{formatMessage(
									globalComponentsEnabled ? messages.switchToJiraCta : messages.switchToCompassCta,
								)}
							</Heading>
							{adminAwareToggle}
						</Header>
						<Body>
							<ComponentsDetails>
								<FormattedMessage
									{...(globalComponentsEnabled
										? messages.jiraDescription
										: messages.compassDescription)}
									values={{
										// @ts-expect-error - Type '(learnMore: string) => JSX.Element' is not assignable to type 'ReactNode'.
										link: (learnMore: string) => (
											<HelpPanelButton
												articleId={
													globalComponentsEnabled
														? WHAT_ARE_JIRA_COMPONENTS_ARTICLE_ID
														: WHAT_ARE_COMPASS_COMPONENTS_ARTICLE_ID
												}
												appearance="link"
												spacing="none"
												onClick={onClickLearnMore}
												fallbackComponent={
													<Button
														href={
															globalComponentsEnabled
																? WHAT_ARE_JIRA_COMPONENTS_SUPPORT_URL
																: WHAT_ARE_COMPASS_COMPONENTS_SUPPORT_URL
														}
														onClick={onClickLearnMore}
														target="_blank"
														appearance="link"
														spacing="none"
													>
														{learnMore}
													</Button>
												}
											>
												{learnMore}
											</HelpPanelButton>
										),
									}}
								/>
							</ComponentsDetails>
							<SwitchInfo>{formatMessage(actionHint)}</SwitchInfo>
						</Body>
					</Container>
				</Section>
				{globalComponentsEnabled && (
					<Section hasSeparator>
						<ButtonItem
							onClick={() => {
								fireUIAnalytics(
									createAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
									}),
									'compassFeedbackCollector',
								);

								openGlobalComponentsFeedbackCollector();
							}}
						>
							{formatMessage(messages.giveFeedback)}
						</ButtonItem>
					</Section>
				)}
			</MenuGroup>
		</ContextualAnalyticsData>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	width: 402,
	paddingTop: token('space.200'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.250'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginBottom: `${token('space.200')}`,

	// Assign margin-right to first header child
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'*:first-child': {
		marginRight: 'auto',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: `${token('space.200')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ComponentsDetails = styled.div({
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwitchInfo = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- PLEASE FIX - ENABLING FLAT LINT CONFIG
	font: token('font.body.small'),
});
